import { FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCreateChurch } from '../hooks/useChurches'
import { geocodeAddress } from '../lib/geocode'
import { apiFetch } from '../lib/api'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Icons } from '../components/ui/Icons'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'
import type { Church } from '@fellowship/shared'

export default function NewChurchPage() {
  const navigate = useNavigate()
  const createChurch = useCreateChurch()

  const [name, setName] = useState('')
  const [denomination, setDenomination] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [description, setDescription] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [geocoding, setGeocoding] = useState(false)
  const [similarChurches, setSimilarChurches] = useState<Church[]>([])
  const [pendingPayload, setPendingPayload] = useState<Parameters<typeof createChurch.mutate>[0] | null>(null)

  function submitCreate(payload: Parameters<typeof createChurch.mutate>[0]) {
    createChurch.mutate(payload, {
      onSuccess: (res) => {
        navigate(`/churches/${res.data.slug}/dashboard`)
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      },
    })
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(undefined)
    setSimilarChurches([])
    setGeocoding(true)

    const fullAddress = `${addressLine1}, ${city}, ${state} ${zip}`
    const coords = await geocodeAddress(fullAddress)
    setGeocoding(false)

    if (!coords) {
      setError('Could not find that address. Please check the city, state, and zip.')
      return
    }

    const payload = {
      name: name.trim(),
      denomination: denomination || 'other',
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim() || undefined,
      city: city.trim(),
      state: state.trim(),
      zip: zip.trim(),
      country: 'US',
      locationLat: coords.lat,
      locationLng: coords.lng,
      email: email.trim(),
      description: description.trim() || undefined,
      websiteUrl: websiteUrl.trim() || undefined,
      phone: phone.trim() || undefined,
    }

    const sp = new URLSearchParams({ name: name.trim(), lat: String(coords.lat), lng: String(coords.lng) })
    const similar = await apiFetch<{ data: Church[] }>(`/churches/similar?${sp}`).catch(() => ({ data: [] as Church[] }))
    if (similar.data.length > 0) {
      setSimilarChurches(similar.data)
      setPendingPayload(payload)
      return
    }

    submitCreate(payload)
  }

  function handleContinueAnyway() {
    if (pendingPayload) {
      setSimilarChurches([])
      submitCreate(pendingPayload)
    }
  }

  const submitting = geocoding || createChurch.isPending

  return (
    <PublicLayout activeNav="Host an event">
      <div className="max-w-[680px] mx-auto px-6 pt-10 pb-20">
        <Link
          to="/host"
          className="inline-flex items-center gap-1.5 text-[13px] text-muted no-underline mb-7"
        >
          <Icons.arrow size={12} className="rotate-180" /> Back
        </Link>

        <div className="eyebrow mb-2">New church</div>
        <h1 className="serif m-0 mb-1.5 text-[40px] tracking-tight">
          List your{' '}
          <span className="serif-i text-primary">church</span>
        </h1>
        <p className="text-sm text-ink-2 m-0 mb-9">
          Your church profile is public. After signup you can post events, manage RSVPs, and invite your team.
        </p>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-0">
          <div className="mb-5">
            <label htmlFor="name" className="label">Church name <span className="text-warn">*</span></label>
            <input
              id="name"
              className="input"
              type="text"
              placeholder="e.g. Grace Community Church"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="mb-5">
            <label htmlFor="denomination" className="label">Tradition / denomination</label>
            <select
              id="denomination"
              className="input cursor-pointer"
              value={denomination}
              onChange={(e) => setDenomination(e.target.value)}
            >
              <option value="">Select a tradition…</option>
              {DENOMINATION_OPTIONS.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-2">
            <label className="label">Address <span className="text-warn">*</span></label>
          </div>
          <div className="mb-3">
            <input
              className="input"
              type="text"
              placeholder="Street address"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <input
              className="input"
              type="text"
              placeholder="Suite, building, unit (optional)"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
            />
          </div>
          <div className="grid gap-2.5 mb-5" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
            <input className="input" type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} required />
            <input className="input" type="text" placeholder="State" value={state} onChange={(e) => setState(e.target.value)} required />
            <input className="input" type="text" placeholder="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} required />
          </div>

          <div className="mb-5">
            <label htmlFor="description" className="label">
              About your church <span className="text-muted font-normal">(optional)</span>
            </label>
            <textarea
              id="description"
              className="input resize-y"
              placeholder="A short description shown on your public profile"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="mb-5">
            <label htmlFor="email" className="label">Contact email <span className="text-warn">*</span></label>
            <input
              id="email"
              className="input"
              type="email"
              placeholder="hello@yourchurch.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-7">
            <div>
              <label htmlFor="website" className="label">Website <span className="text-muted font-normal">(optional)</span></label>
              <input
                id="website"
                className="input"
                type="url"
                placeholder="https://yourchurch.org"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="phone" className="label">Phone <span className="text-muted font-normal">(optional)</span></label>
              <input
                id="phone"
                className="input"
                type="tel"
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          {similarChurches.length > 0 && (
            <div className="rounded-[14px] border border-line p-4 mb-5" style={{ background: 'var(--surface)' }}>
              <p className="text-[13px] font-medium text-ink-1 mb-2">
                A church with a similar name already exists nearby:
              </p>
              <ul className="mb-3 space-y-1">
                {similarChurches.map((c) => (
                  <li key={c.id} className="text-[13px]">
                    <Link to={`/churches/${c.slug}`} className="text-primary underline">{c.name}</Link>
                    {c.city ? ` — ${c.city}${c.state ? `, ${c.state}` : ''}` : ''}
                    {!c.claimed && (
                      <>
                        {' · '}
                        <Link to={`/churches/${c.slug}/claim`} className="text-primary underline">
                          Claim it instead
                        </Link>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[12px] text-ink-2 mb-3">
                If this is a different church or location, you can still proceed.
              </p>
              <button
                type="button"
                onClick={handleContinueAnyway}
                disabled={createChurch.isPending}
                className="btn btn--sm"
              >
                {createChurch.isPending ? 'Creating…' : 'Continue anyway'}
              </button>
            </div>
          )}

          {error && (
            <p className="text-[13px] text-warn mb-4">{error}</p>
          )}

          {similarChurches.length === 0 && (
            <button
              type="submit"
              disabled={submitting || !name.trim() || !email.trim() || !addressLine1.trim() || !city.trim() || !state.trim() || !zip.trim()}
              className="btn btn--lg self-start min-w-[180px]"
            >
              {submitting ? (geocoding ? 'Finding address…' : 'Creating…') : 'Create church profile'} <Icons.arrow size={14} />
            </button>
          )}

          <p className="mt-4 text-xs text-muted leading-[1.5]">
            A subscription is required to post events. You can start your free trial from the dashboard after signup.
          </p>
        </form>
      </div>
    </PublicLayout>
  )
}
